import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import FolderOpen from "@material-ui/icons/FolderOpen";
import InsertDriveFileOutlined from "@material-ui/icons/InsertDriveFileOutlined";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import DescriptionOutlined from "@material-ui/icons/DescriptionOutlined";
import PlayCircleOutline from "@material-ui/icons/PlayCircleOutline";
import HeadsetMicOutlined from "@material-ui/icons/HeadsetMicOutlined";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import EmojiObjectsOutlined from "@material-ui/icons/EmojiObjectsOutlined";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import ExpandMore from "@material-ui/icons/ExpandMore";
import Block from "@material-ui/icons/Block";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";

const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        panel: { display: "flex", flexDirection: "column", gap: "16px" },
        card: { ...s.card, padding: "18px 18px 16px", textAlign: "left" },
        heading: { margin: "0 0 14px", fontSize: "17px", fontWeight: 700, color: t.textStrong, display: "flex", alignItems: "center", gap: "8px" },
        steps: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "14px" },
        step: { display: "flex", gap: "12px", alignItems: "flex-start" },
        stepNumber: {
            flexShrink: 0, width: "30px", height: "30px", borderRadius: "50%", display: "flex",
            alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "14px"
        },
        n1: { backgroundColor: t.info.solid, color: t.info.onSolid },
        n2: { backgroundColor: t.success.solid, color: t.success.onSolid },
        n3: { backgroundColor: t.warning.solid, color: t.warning.onSolid },
        n4: { backgroundColor: "#8B5CF6", color: "#FFFFFF" },
        nBlock: { backgroundColor: t.danger.solid, color: t.danger.onSolid },
        blockIcon: { flexShrink: 0, color: t.danger.fg, "& svg": { width: "24px", height: "24px" } },
        newTag: { marginLeft: "6px", padding: "1px 6px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, backgroundColor: t.danger.bg, color: t.danger.fg, border: "1px solid "+t.danger.border, verticalAlign: "middle" },
        itemTitle: { margin: 0, fontSize: "14px", fontWeight: 700, color: t.textStrong },
        itemText: { margin: "2px 0 0", fontSize: "13px", lineHeight: "18px", color: t.textMuted },
        typeIcon: { flexShrink: 0, color: t.dark ? "#FAAF42" : "#D97706", "& svg": { width: "24px", height: "24px" } },
        links: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "12px" },
        link: {
            ...s.focusRing,
            display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "14px", fontWeight: 600,
            color: t.link + " !important", textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
            "& svg": { width: "22px", height: "22px", color: t.textMuted }
        },
        accordion: { borderTop: "1px solid "+t.border, "&:first-of-type": { borderTop: "none" } },
        accordionButton: {
            ...s.focusRing,
            width: "100%", display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 0",
            background: "transparent", border: "none", color: t.text, cursor: "pointer", textAlign: "left"
        },
        accordionIcon: { color: t.dark ? t.link : t.accent, flexShrink: 0, "& svg": { width: "22px", height: "22px" } },
        chevron: { marginLeft: "auto", color: t.textMuted, transition: "transform 0.2s", flexShrink: 0 },
        chevronOpen: { transform: "rotate(180deg)" },
        accordionBody: { padding: "0 0 12px 32px", fontSize: "13px", lineHeight: "19px", color: t.textMuted, "& ul": { margin: 0, paddingLeft: "18px" }, "& li": { marginBottom: "4px" } }
    };
});

function NeedHelp({ classes }){
    return (
        <section className={classes.card} aria-labelledby="far-help-heading">
            <h3 className={classes.heading} id="far-help-heading">Need Help?</h3>
            <ul className={classes.links}>
                <li><RouterLink className={classes.link} to="/help"><DescriptionOutlined aria-hidden="true" />View Documentation</RouterLink></li>
                <li><RouterLink className={classes.link} to="/user/tutorials"><PlayCircleOutline aria-hidden="true" />Watch Video Tutorial</RouterLink></li>
                <li><RouterLink className={classes.link} to="/user/support"><HeadsetMicOutlined aria-hidden="true" />Contact Support</RouterLink></li>
            </ul>
        </section>
    );
}

function HowItWorks({ classes }){
    const steps = [
        { title: "Define File Conditions", text: "Match files by name (prefix/suffix) and/or the folder they come from.", cls: classes.n1 },
        { title: "Set Authorized Recipients", text: "Specify which email addresses are allowed.", cls: classes.n2 },
        { title: "Block Specific Recipients", text: "Optionally block email addresses or whole domains. Blocked recipients can't receive matching files, even if they are also authorized.", cls: classes.nBlock },
        { title: "Send From Anywhere", text: "Rules apply to uploads and to files sent from My Docs. Every recipient of a matched file is checked.", cls: classes.n3 },
        { title: "Get Notified", text: "When a file matches a rule, recipients are validated before sending.", cls: classes.n4 }
    ];
    return (
        <section className={classes.card} aria-labelledby="far-how-heading">
            <h3 className={classes.heading} id="far-how-heading">How File Attachment Rules Work</h3>
            <ol className={classes.steps}>
                {
                    steps.map((step, key) => (
                        <li className={classes.step} key={key}>
                            <span className={classes.stepNumber+" "+step.cls} aria-hidden="true">{key + 1}</span>
                            <div>
                                <p className={classes.itemTitle}>{step.title}</p>
                                <p className={classes.itemText}>{step.text}</p>
                            </div>
                        </li>
                    ))
                }
            </ol>
        </section>
    );
}

function RuleTypes({ classes }){
    const types = [
        { Icon: FolderOpen, title: "Sendlinx folder", text: "Match files sent from a My Docs folder or its subfolders. Checked on the web portal." },
        { Icon: FolderOpen, title: "Local folder", text: "Match files attached from a folder on the sender's computer. Checked by the Outlook plug-in only — a browser cannot read local folder paths." },
        { Icon: InsertDriveFileOutlined, title: "Prefix", text: "Match files that start with a specific text." },
        { Icon: InsertDriveFileOutlined, title: "Suffix", text: "Match files that end with a specific text." },
        { Icon: FileCopyOutlined, title: "Combined", text: "Use prefix, suffix and a folder together." },
        { Icon: Block, title: "Recipient Restrictions", isNew: true, text: "Block specific email addresses or domains (competitor.com) for files that match the rule. Exclusions override authorized recipients; the rule decides whether anyone can override them." }
    ];
    return (
        <section className={classes.card} aria-labelledby="far-types-heading">
            <h3 className={classes.heading} id="far-types-heading">Rule Types</h3>
            <ul className={classes.steps}>
                {
                    types.map((type, key) => (
                        <li className={classes.step} key={key}>
                            <span className={type.isNew ? classes.blockIcon : classes.typeIcon} aria-hidden="true"><type.Icon /></span>
                            <div>
                                <p className={classes.itemTitle}>{type.title}{ type.isNew ? <span className={classes.newTag}>New</span> : null }</p>
                                <p className={classes.itemText}>{type.text}</p>
                            </div>
                        </li>
                    ))
                }
            </ul>
        </section>
    );
}

function Accordion({ classes, Icon, title, text, children, id }){
    const [open, setOpen] = React.useState(false);
    return (
        <div className={classes.accordion}>
            <button type="button" className={classes.accordionButton} aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
                <span className={classes.accordionIcon} aria-hidden="true"><Icon /></span>
                <span>
                    <span className={classes.itemTitle} style={{display: "block"}}>{title}</span>
                    <span className={classes.itemText} style={{display: "block"}}>{text}</span>
                </span>
                <ExpandMore className={classes.chevron+" "+(open ? classes.chevronOpen : "")} aria-hidden="true" />
            </button>
            { open ? <div className={classes.accordionBody} id={id}>{children}</div> : null }
        </div>
    );
}

function HelpfulInformation({ classes }){
    return (
        <section className={classes.card} aria-labelledby="far-info-heading">
            <h3 className={classes.heading} id="far-info-heading"><InfoOutlined aria-hidden="true" />Helpful Information</h3>
            <Accordion classes={classes} id="far-info-how" Icon={VerifiedUser} title="How File Attachment Rules Work" text="Learn how rules validate files based on file name, location and recipients.">
                <ul>
                    <li>Each rule has file conditions (prefix, suffix, folder) and a list of authorized recipients.</li>
                    <li>When a file matches a rule, every recipient must be on that rule's list.</li>
                    <li>A rule can also block email addresses or domains. A blocked recipient is never authorized for matching files, even if it is on the authorized list. Domains match exactly (competitor.com does not include mail.competitor.com).</li>
                    <li>On the web portal, prefix, suffix and Sendlinx folder are checked in real time. Sendlinx folders apply to files sent from My Docs; uploaded files have no Sendlinx folder.</li>
                    <li>Local folder conditions (C:\...) are checked by the Outlook plug-in only, because browsers don't share local folder paths.</li>
                </ul>
            </Accordion>
            <Accordion classes={classes} id="far-info-best" Icon={EmojiObjectsOutlined} title="Best Practices" text="Tips for configuring effective and secure rules.">
                <ul>
                    <li>Give rules clear names such as "ABC Inc Invoices".</li>
                    <li>Keep "Enforce all matching rules" on so a weaker rule cannot bypass a stricter one.</li>
                    <li>Require an override reason and keep override logging on for accountability.</li>
                </ul>
            </Accordion>
            <Accordion classes={classes} id="far-info-ux" Icon={PeopleOutline} title="User Experience" text="See what users will experience when a rule is triggered.">
                <ul>
                    <li>Matched files show a green, amber or red status on the Send page.</li>
                    <li>Unauthorized recipients are flagged and Send stays disabled until the issue is resolved (unless "Warn only" is selected).</li>
                    <li>Blocked recipients are marked "Blocked by FAR exclusion". Depending on the rule they must be removed, need an administrator's authorization, or follow the normal exception settings. "Warn only" never lets a hard block through.</li>
                    <li>Every exception is recorded in the Audit Log.</li>
                </ul>
            </Accordion>
        </section>
    );
}

function FarHelpPanel({ variant }){
    const classes = useStyles();
    return (
        <aside className={classes.panel} aria-label="File Attachment Rules help">
            {
                variant === "settings" ?
                    <HelpfulInformation classes={classes} />
                :
                    <>
                        <HowItWorks classes={classes} />
                        <RuleTypes classes={classes} />
                    </>
            }
            <NeedHelp classes={classes} />
        </aside>
    );
}

FarHelpPanel.defaultProps = { variant: "rules" };
FarHelpPanel.propTypes = { variant: PropTypes.oneOf(["rules", "settings", "audit"]) };

export default FarHelpPanel;
